import { Component, EventEmitter, HostBinding, Input, Output, output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonName } from '@app/components/booking-flow/booking-flow.component';
import { EnterYourInformationComponent } from '@app/components/booking-steps/enter-your-information/enter-your-information.component';
import { Sheet } from '@app/enums/sheet.enum';
import { AppointmentRequest } from '@app/models/appointmentRequest.model';
import { Client } from '@app/models/client.model';
import { Site } from '@app/models/site.model';
import { UserAccount } from '@app/models/userAccount.model';
import { ContentService } from '@app/services/content.service';
import { ErrorDialogService } from '@app/services/error-dialog.service';
import { FeatureFlagService, FeatureKey } from '@app/services/feature-flag.service';
import { APPOINTMENT_REQUEST, CUSTOMER_ID, LocalStorageService, SITE } from '@app/services/local-storage.service';
import { NavigationService } from '@app/services/navigation.service';
import { UserAccountService } from '@app/services/user-account.service';

@Component({
    selector: 'app-create-account-form',
    templateUrl: './create-account-form.component.html',
    styleUrls: ['./create-account-form.component.css'],
    standalone: false,
})
export class CreateAccountFormComponent {
    @HostBinding('class') classes = 'create-account-component-container';
    @ViewChild(EnterYourInformationComponent, { static: true }) enterYourInformationComponent: EnterYourInformationComponent | undefined;
    @Input() public actionButtonText: string = 'Create Account';
    @Input() public showPassword: boolean = true;
    @Output() closeSheet = new EventEmitter<Sheet>();
    isSaving = false;
    actionButtonName = '';
    private site: Site;
    public userAccountFeatureEnabled: boolean = true;
    private redesignFeatureEnabled: boolean = false;
    public readonly accountCreated = output<UserAccount>();

    constructor(
        private localStorageService: LocalStorageService,
        private router: Router,
        public contentService: ContentService,
        private userAccountService: UserAccountService,
        private errorDialogService: ErrorDialogService,
        private featureFlagService: FeatureFlagService,
        private navigationService: NavigationService
    ) {
        this.actionButtonName =
            this.contentService.selectedStepIndex === this.contentService.steps?.find((x) => x.stepRoute === 'booking/client')?.index
                ? 'Review Details'
                : 'Create Account';
        this.site = this.localStorageService.get(SITE) as Site;
        this.userAccountFeatureEnabled = this.featureFlagService.getFeatureFlag(FeatureKey.UserAccounts);
        this.redesignFeatureEnabled = this.featureFlagService.getFeatureFlag(FeatureKey.Redesign);
    }

    public closeBottomSheet() {
        this.accountCreated.emit(this.userAccountService.getCurrentUser()!);
    }

    // The dialog renders whatever it is handed, so an error whose body carries no `message` used to
    // produce a dialog with a title and an empty body - the customer is told something went wrong
    // but not what to do about it.
    //
    // The wording deliberately claims no more than is known. This fires for ANY error the backend
    // does not explain, and the two likeliest are opposites: a duplicate email, where retrying can
    // never succeed and logging in is the way out, and a network failure, where the account may
    // actually have been created. "Could not confirm" covers both without asserting either, and
    // naming the log-in route stops a customer with an existing account looping on retry.
    public static readonly GENERIC_CREATE_ACCOUNT_ERROR =
        'We could not confirm account creation. If you already have an account, please log in; otherwise, please try again.';

    // `||` alone is not enough: a whitespace-only string is truthy, so it would reach the dialog and
    // render the same visually blank body this fallback exists to prevent. A non-string is possible
    // too - `error` is whatever the response body deserialised to, and on a network failure it is a
    // ProgressEvent with no `message` at all.
    private static toDisplayableError(message: unknown): string {
        return (typeof message === 'string' && message.trim()) || CreateAccountFormComponent.GENERIC_CREATE_ACCOUNT_ERROR;
    }

    public onReviewDetails() {
        const enterYourInfoComponent = this.getEnterYourInformationComponent();
        this.isSaving = enterYourInfoComponent?.areFieldsValid() ?? false;
        if (this.isSaving) {
            const formValue = enterYourInfoComponent?.formGroup.value;
            const client = new Client(formValue);
            const pwd = formValue.password as string;
            const customerID = this.localStorageService.getString(CUSTOMER_ID);
            //Below NOSONAR comment is to escape from SonarCloud Code Analysis
            const customerIdWithoutPrefix = customerID?.replace(/.*?(\d+)/, '$1'); //NOSONAR
            this.userAccountService.create(client, pwd, customerIdWithoutPrefix).subscribe({
                next: () => {
                    this.contentService.isAccountCreatedFromClientScreen = true;
                    this.userAccountService.handleAccountCreation(
                        this.site,
                        this.contentService.selectedStepIndex,
                        this.navigateToRoute.bind(this)
                    );
                },
                error: (ex) => {
                    this.isSaving = false;
                    this.openErrorDialog(CreateAccountFormComponent.toDisplayableError(ex.error?.message));
                },
                complete: () => {
                    if (this.redesignFeatureEnabled) {
                        this.isSaving = false;
                        this.navigationService.setNavigationButtonName(ButtonName.ReviewDetails);
                        this.closeSheet.emit(Sheet.Main);
                        this.closeSheet.emit(Sheet.Create);
                        this.accountCreated.emit(this.userAccountService.getCurrentUser()!);
                    } else {
                        this.closeBottomSheet();
                    }
                },
            });
        }
    }

    public onContinue() {
        const enterYourInfoComponent = this.getEnterYourInformationComponent();
        const isValid = enterYourInfoComponent?.areFieldsValid() ?? false;
        if (isValid) {
            const formValue = enterYourInfoComponent?.formGroup.value;
            const appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
            appointmentRequest.client = new Client(formValue);
            this.localStorageService.set(APPOINTMENT_REQUEST, appointmentRequest);
        }
        this.closeSheet.emit(Sheet.Main);
        this.closeSheet.emit(Sheet.Checkout);
    }

    protected openErrorDialog(message: string) {
        this.errorDialogService.openErrorDialog(message);
    }

    protected getEnterYourInformationComponent(): EnterYourInformationComponent | undefined {
        return this.enterYourInformationComponent;
    }

    private navigateToRoute(selectedStep: any) {
        void this.router.navigate([selectedStep.stepRoute], {
            queryParams: {
                DSID: this.localStorageService.getString(CUSTOMER_ID),
            },
        });

        this.contentService.isStepCompleted = selectedStep.index;
    }
}
